import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, fromEvent, merge, timer } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface SessionTimeoutOptions {
  idleMs?: number;        // total inactivity before logout
  onTimeout?: () => void; // e.g., logout + navigate to /login
}

@Injectable({ providedIn: 'root' })
export class SessionTimeoutService implements OnDestroy {
  private options: Required<SessionTimeoutOptions> = {
    idleMs: parseInt(environment.SessionTimeout),
    onTimeout: () => {
      /* default no-op */
    }
  };

  private stop$ = new Subject<void>();

  private warnSub?: Subscription;
  private timeoutSub?: Subscription;
  private countdownSub?: Subscription;

  private warningSubject = new Subject<void>();
  warning$ = this.warningSubject.asObservable();

  remainingSecondsSubject = new BehaviorSubject<number | null>(null);
  remainingSeconds$ = this.remainingSecondsSubject.asObservable();

  constructor(private zone: NgZone) {}

  ngOnDestroy(): void {
    this.stop();
  }

  start(opts?: SessionTimeoutOptions): void {
    this.options = { ...this.options, ...opts };

    // Track user activity and reset timers
    this.zone.runOutsideAngular(() => {
      const activity$ = merge(
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'click'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'scroll', { passive: true } as AddEventListenerOptions),
        fromEvent(document, 'touchstart')
      ).pipe(takeUntil(this.stop$));

      activity$.subscribe(() => this.resetTimers());
    });

    this.resetTimers();
  }

  stop(): void {
    this.stop$.next();
    this.clearTimers();
    this.remainingSecondsSubject.next(null);
  }

  public resetTimers(): void {
    this.clearTimers();
    if (isNaN(this.options.idleMs) || this.options.idleMs <= 0) {
      return;
    }
    this.startCountdown();

    this.timeoutSub = timer(this.options.idleMs).subscribe(() => {
      this.zone.run(() => this.handleTimeout());
    });
  }

  private handleTimeout(): void {
    this.clearTimers();
    this.remainingSecondsSubject.next(0);
    this.options.onTimeout?.();
  }

  private clearTimers(): void {
    this.warnSub?.unsubscribe();
    this.timeoutSub?.unsubscribe();
    this.countdownSub?.unsubscribe();
    this.warnSub = this.timeoutSub = this.countdownSub = undefined;

  }
  private startCountdown(): void {
    if (isNaN(this.options.idleMs) || this.options.idleMs <= 0) {
      this.zone.run(() => this.remainingSecondsSubject.next(0));
      return;
    }
    const totalSeconds = Math.ceil(this.options.idleMs / 1000);

    if (totalSeconds <= 0) {
      this.zone.run(() => this.remainingSecondsSubject.next(0));
      return;
    }

    this.zone.run(() => this.remainingSecondsSubject.next(totalSeconds));

    this.zone.runOutsideAngular(() => {
      this.countdownSub = timer(1000, 1000).pipe(take(totalSeconds)).subscribe(tick => {
        const remaining = totalSeconds - tick - 1;
        this.zone.run(() => this.remainingSecondsSubject.next(Math.max(remaining, 0)));
      });
    });
  }
}
